import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import LanguageToggle from '../components/LanguageToggle';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(detail.map(e => e.msg).join(' '));
      } else {
        setError('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center px-4">
      <div className="absolute top-4 end-4">
        <LanguageToggle />
      </div>

      <div className="w-full max-w-md">
        <div className="bg-carbon border border-[#27272A] rounded-lg p-8 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
          <div className="text-center mb-8">
            <h1 className="font-heading text-4xl font-black tracking-tight text-safety-amber mb-2">
              {t('Anime Safety Guide', 'دليل السلامة الأنيمي')}
            </h1>
            <p className="text-[#A1A1AA]">
              {t('Admin Dashboard', 'لوحة تحكم المسؤول')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#EDEDED]">
                {t('Email', 'البريد الإلكتروني')}
              </Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-obsidian border-[#27272A] text-[#EDEDED] focus:border-safety-amber focus:ring-safety-amber"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#EDEDED]">
                {t('Password', 'كلمة المرور')}
              </Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-obsidian border-[#27272A] text-[#EDEDED] focus:border-safety-amber focus:ring-safety-amber"
                required
              />
            </div>

            {error && (
              <div className="bg-signal-red/10 border border-signal-red/30 rounded p-3 text-signal-red text-sm">
                {error}
              </div>
            )}

            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-safety-amber hover:bg-safety-amber/90 text-obsidian font-bold transition-all hover:scale-[1.02]"
            >
              {loading ? t('Logging in...', 'جارٍ تسجيل الدخول...') : t('Login', 'تسجيل الدخول')}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
